import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

import { ComparisonSection } from "./sections/comparison";
import { FAQSection } from "./sections/faq";
import { FeatureAIAssistantSection } from "./sections/feature-ai-assistant";
import { FeatureAIWriterSection } from "./sections/feature-ai-writer";
import { FeatureATSScoreSection } from "./sections/feature-ats-score";
import { FeatureCoverLetterSection } from "./sections/feature-cover-letter";
import { FeatureJobMatchSection } from "./sections/feature-job-match";
import { FinalCTASection } from "./sections/final-cta";
import { HeroSection } from "./sections/hero";
import { HowItWorksSection } from "./sections/how-it-works";
import { MidCTASection } from "./sections/mid-cta";
import { MissionSection } from "./sections/mission";
import { PricingSection } from "./sections/pricing";
import { SocialProofSection } from "./sections/social-proof";
import { TemplatesSection } from "./sections/templates";
import { TestimonialsSection } from "./sections/testimonials";

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How much does CareerBldr cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "CareerBldr offers four plans starting at $9/month. The Pro plan at $19/month includes full AI writing, ATS scoring, job matching, cover letters, and DOCX export. New subscriptions include a 3-day free trial.",
      },
    },
    {
      "@type": "Question",
      name: "Who runs CareerBldr?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "CareerBldr is built by Studio Algorithm, a mission-driven organization making professional career tools accessible to everyone on a best-effort basis.",
      },
    },
    {
      "@type": "Question",
      name: "How does the AI resume writing work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "CareerBldr uses the latest AI models to generate, improve, and tailor resume content. You can generate bullet points from your job title, improve existing text with one click, quantify achievements, change tone, and have AI write entire sections. All content is original and specific to your experience.",
      },
    },
    {
      "@type": "Question",
      name: "What is ATS and why does it matter?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "ATS (Applicant Tracking System) is software used by 85%+ of companies to screen resumes before a human ever sees them. If your resume doesn't parse correctly or lacks the right keywords, it gets filtered out. CareerBldr's templates are ATS-verified, and real-time ATS scoring is included on Pro and above.",
      },
    },
    {
      "@type": "Question",
      name: "Will my resume pass ATS screening?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "All CareerBldr templates are designed and tested for ATS compatibility across Greenhouse, Lever, Workday, and Taleo. A real-time 0-100 ATS score with specific feedback is included on Pro and above.",
      },
    },
    {
      "@type": "Question",
      name: "Can I export my resume as PDF and Word/DOCX?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "PDF export is available on all plans. DOCX (Word) export is available on Pro and above. Both formats are optimized for ATS parsing.",
      },
    },
    {
      "@type": "Question",
      name: "How does job description matching work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Paste any job posting into CareerBldr and the AI instantly analyzes it against your resume. It identifies missing keywords, highlights skill gaps, and suggests specific improvements.",
      },
    },
    {
      "@type": "Question",
      name: "Is CareerBldr the best AI resume builder?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "CareerBldr offers a comprehensive AI feature set: AI writing, ATS scoring, job description matching, auto-tailoring, cover letter generation, and an AI chat assistant. Plans start at $9/month with a 3-day free trial.",
      },
    },
    {
      "@type": "Question",
      name: "Is there a free trial?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. New subscribers get a 3-day free trial. A payment method is required at checkout. Cancel before the trial ends and you will not be charged.",
      },
    },
  ],
};

const softwareStructuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "CareerBldr",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: "https://careerbldr.com",
  sameAs: ["https://x.com/careerbldr_"],
  description:
    "AI-powered resume builder with ATS scoring, job description matching, cover letter generation, and AI writing tools.",
  offers: {
    "@type": "AggregateOffer",
    lowPrice: "9",
    highPrice: "79",
    priceCurrency: "USD",
  },
};

export const HomePage = () => {
  const { i18n } = useLingui();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [hash]);

  return (
    <main className="relative isolate overflow-x-hidden bg-background">
      <Helmet prioritizeSeoTags>
        <html lang={i18n.locale} />

        <title>
          {t`CareerBldr`} - {t`AI Resume Builder That Gets You Hired`}
        </title>

        <meta
          name="description"
          content="Build ATS-optimized resumes with AI. Write content, check your ATS score, match to job descriptions, and generate cover letters. Plans start at $9/month with a 3-day free trial."
        />

        <meta property="og:title" content="CareerBldr - AI Resume Builder That Gets You Hired" />
        <meta
          property="og:description"
          content="Build ATS-optimized resumes with AI. Write content, check your ATS score, match to job descriptions, and generate cover letters in minutes."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://careerbldr.com" />
        <meta property="og:image" content="https://careerbldr.com/screenshots/builder.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@careerbldr_" />
        <meta name="twitter:creator" content="@careerbldr_" />
        <meta name="twitter:title" content="CareerBldr - AI Resume Builder That Gets You Hired" />
        <meta
          name="twitter:description"
          content="Build ATS-optimized resumes with AI. Trusted by 42,000+ professionals."
        />
        <meta name="twitter:image" content="https://careerbldr.com/screenshots/builder.jpg" />

        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareStructuredData)}</script>
      </Helmet>

      <HeroSection />
      <SocialProofSection />

      <FeatureAIWriterSection />
      <FeatureATSScoreSection />
      <FeatureJobMatchSection />
      <FeatureCoverLetterSection />
      <FeatureAIAssistantSection />

      <MidCTASection />

      <HowItWorksSection />
      <TemplatesSection />
      <ComparisonSection />
      <PricingSection />
      <TestimonialsSection />
      <MissionSection />
      <FAQSection />
      <FinalCTASection />
    </main>
  );
};
